import React, { Suspense, lazy, useMemo } from 'react';

import type { QueryOptions } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { green, red } from '@mui/material/colors';
import type { Theme } from '@mui/material/styles';
import type { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_REORDER_COL_DEF,
} from '@mui/x-data-grid-premium';
import { LayoutGrid, Rows3, Search, Trash2Icon } from 'lucide-react';
import { Route, Routes } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  type GetLotsListQuery,
  type Lots_Bool_Exp,
  type Lots_Order_By,
  Order_By,
} from '@graphql/__generated__/graphql';
import { ListingCell } from '@modules/_shared/components/data-grid/ListingCell';
import { ListingPriceCell } from '@modules/_shared/components/data-grid/ListingPriceCell';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import type { QuickFilterDefinition } from '@modules/_shared/components/filters/Filters';
import { getValueFromPath } from '@modules/_shared/components/filters/SchemaProvider';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { LoadingCheckBox } from '@modules/_shared/components/LoadingCheckbox';
import { LotCardCarousel } from '@modules/_shared/components/LotCardCarousel';
import {
  LotCardInfo,
  getLotStatusTagVariant,
} from '@modules/_shared/components/LotCardInfo';
import { LotSelect } from '@modules/_shared/components/LotsSelect';
import RaGrid from '@modules/_shared/components/RaGrid';
import { SmartListTitle } from '@modules/_shared/components/SmartListTitle';
import { Tag } from '@modules/_shared/components/Tag';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserChip } from '@modules/_shared/components/UserChip';
import { useBulkEditMode } from '@modules/_shared/hooks/useBulkEditMode';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useTogglePortalListing } from '@modules/_shared/hooks/useTogglePortalListing';
import {
  type Me,
  useAppData,
} from '@modules/_shared/providers/AppDataProvider';
import { formatSurfaceValue } from '@modules/_shared/utils/formatting';
import { getIncludedIds } from '@modules/_shared/utils/gridRowSelection';
import {
  canEditListing,
  userCanPublishPortalListing,
} from '@modules/_shared/utils/listingPermissions';
import {
  parseOrderByParam,
  withTiebreaker,
} from '@modules/_shared/utils/listUrlParams';
import {
  toScopedSearchParams,
  useScopedSearchParams,
} from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import {
  extractPropertyOrientations,
  getOrientationTranslation,
} from '@modules/_shared/utils/propertyTemplateFunctions';
import { getAgentFilter } from '@modules/_shared/utils/quickFilters';
import { ListingsExport } from '@modules/listings/ListingsExport';
import ListingsImport from '@modules/listings/ListingsImport';
import {
  DELETE_LOTS,
  GET_LOTS_LIST,
  GET_LOTS_LIST_COUNT,
  REORDER_LOTS,
} from '@modules/listings/lotsQueries';
import {
  type IntlLocale,
  type Translate,
  useLocale,
} from '@shared/hooks/locale';
import { lazyRetry } from '@shared/utils/lazyRetry';

const CreateListingDrawer = lazy(() =>
  lazyRetry(() => import('@modules/listings/CreateListingDrawer')),
);
const ListingDrawer = lazy(() =>
  lazyRetry(() => import('@modules/listings/ListingDrawer')),
);

const GRID_PAGE_SIZE = 24;
const LIST_PAGE_SIZE = 25;

export const initialListingsListQuery = (
  searchParams: URLSearchParams,
  reorderable: boolean,
  developmentId: string | undefined,
): QueryOptions => {
  const whereClause = prepareWhereClauseQuery(
    JSON.parse(searchParams.get('where') || '{}'),
  );

  const where: Lots_Bool_Exp = {
    ...whereClause,
    development_id: developmentId != null ? { _eq: developmentId } : undefined,
  };

  const initialOrderBy: Lots_Order_By[] = reorderable
    ? [{ order_nr: Order_By.Asc }]
    : [{ created_at: Order_By.Desc }];

  const order_by = withTiebreaker<Lots_Order_By>(
    parseOrderByParam(searchParams.get('order_by'), initialOrderBy),
    { id: Order_By.Asc },
    'id',
  );
  const defaultLimit =
    searchParams.get('view') === 'list' ? LIST_PAGE_SIZE : GRID_PAGE_SIZE;
  const { limit, offset } = getPaginationVariables(searchParams, defaultLimit);

  return {
    query: GET_LOTS_LIST,
    variables: {
      where,
      offset,
      limit,
      order_by,
    },
  };
};

const quickFilters = (t: Translate, me: Me): QuickFilterDefinition[] => [
  {
    label: t('Created'),
    path: ['lots_bool_exp', 'created_at'],
  },
  {
    label: t('Signed'),
    path: ['lots_bool_exp', 'signed_at'],
  },
  { label: t('Status'), path: ['lots_bool_exp', 'status', '_in'] },
  {
    label: t('Source'),
    path: ['lots_bool_exp', 'origin', 'id', '_in'],
    displayedColumn: 'label',
    filter: {
      path: ['name', 'type', '_in'],
      value: 'lot_origin_types',
    },
  },
  {
    label: t('Agent'),
    path: ['lots_bool_exp', 'broker'],
    filter: getAgentFilter(me),
  },
  {
    label: t('Seller'),
    path: ['lots_bool_exp', 'sellers', 'seller'],
  },
  // Shadow-banned FSBO advertiser (#24256), tenant admins only. "No" clears
  // the review queue of listings that will never go live; "Yes" lists them.
  // Rendered by FsboBlockedAdvertiserFilter (picked on this path), which
  // matches a blocked creator OR a blocked seller: `sellers` is an array
  // relationship, so "No" has to be a negated EXISTS, which the generic
  // null-check filter cannot express — it would drop every lot with no
  // seller at all and match a two-seller lot on both sides.
  ...(me?.is_admin === true
    ? [
        {
          label: t('Blocked advertiser'),
          path: ['lots_bool_exp', 'sellers', 'seller', 'fsbo_blocked_at'],
          nullCheckMode: 'not-null' as const,
        },
      ]
    : []),
  {
    label: t('Team'),
    path: ['lots_bool_exp', 'broker', 'teams_users', 'team'],
  },
  {
    label: t('Location'),
    path: ['lots_bool_exp', 'property', 'places'],
  },
  {
    label: t('Buy / Rent'),
    path: ['lots_bool_exp', 'offer_type', '_in'],
  },
  {
    label: t('Sale price'),
    path: ['lots_bool_exp', 'sale_price'],
    disabled: where => {
      const value: string[] | undefined = getValueFromPath(
        ['lots_bool_exp', 'offer_type', '_in'],
        where,
      );

      return value?.includes('rent') ?? false;
    },
  },
  {
    label: t('Rent'),
    path: ['lots_bool_exp', 'rent_net'],
    disabled: where => {
      const value: string[] | undefined = getValueFromPath(
        ['lots_bool_exp', 'offer_type', '_in'],
        where,
      );

      return value?.includes('sell') ?? false;
    },
  },
  {
    label: t('Online'),
    path: ['lots_bool_exp', 'portal_listings', 'live'],
  },
  {
    label: t('MLS'),
    path: ['lots_bool_exp', 'shared_on_mls_at'],
    nullCheckMode: 'not-null',
  },
  {
    label: t('Property type'),
    path: ['lots_bool_exp', 'property', 'property_type', 'name', '_in'],
    displayedColumn: 'label',
    filter: {
      path: ['property_type', 'main_type', '_is_null'],
      value: false,
    },
    orderBy: [
      {
        composite_type: Order_By.AscNullsLast,
      },
      {
        type: Order_By.Asc,
      },
      {
        label: Order_By.Asc,
      },
    ],
  },
  {
    label: t('Rooms'),
    path: ['lots_bool_exp', 'property', 'number_of_rooms'],
  },
  {
    label: t('Offers'),
    path: ['lots_bool_exp', 'offers_aggregate', 'count', 'predicate'],
  },
  {
    label: t('Enquiries'),
    path: ['lots_bool_exp', 'enquiries_aggregate', 'count', 'predicate'],
  },
  {
    label: t('Exclusivity'),
    path: ['lots_bool_exp', 'is_exclusive'],
  },
  {
    label: t('Time to sell'),
    path: ['lots_bool_exp', 'expected_time_to_sell', '_in'],
  },
];

type Lot = GetLotsListQuery['lots'][number];

// Shadow-banned FSBO advertiser (#24256): their listings sit in pending
// review and never go live, so a reviewer must be able to tell them apart
// from a real pending review at a glance.
// Same set the lots trigger and the reviewer enforce on: the creator or any
// seller (an FSBO lot may carry no seller row at all).
const isBlockedAdvertiser = (lot: Lot) =>
  lot.created_by_user?.fsbo_blocked_at != null ||
  lot.sellers.some(s => s.seller?.fsbo_blocked_at != null);

// Tenant admins only: the block is their decision and their queue to clean.
// `overlay` picks the dark variant that reads over a photo (like the
// online/offline tag it sits under on the card).
const BlockedAdvertiserTag = ({
  lot,
  overlay = false,
}: {
  lot: Lot;
  overlay?: boolean;
}) => {
  const { t } = useLocale();
  const { me } = useAppData();
  if (me?.is_admin !== true || !isBlockedAdvertiser(lot)) {
    return null;
  }
  return (
    <Tag status={overlay ? 'dark_error' : 'error'} noBorder={overlay}>
      {t('Blocked advertiser')}
    </Tag>
  );
};

type ListingsProps = {
  developmentId?: string;
  detailedView?: boolean;
  hideTitle?: boolean;
  reorderable?: boolean;
  queryParamsScope?: string;
};

const LotCard = React.memo(({ data }: { data: Lot }) => {
  const { t } = useLocale();

  const { sellers, broker } = data;
  const seller = sellers[0]?.seller;

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: '100%',
      }}
    >
      <LotCardCarousel
        lot={data}
        statusExtra={<BlockedAdvertiserTag lot={data} overlay />}
      />
      <CardActionArea sx={{ flexGrow: 1 }}>
        <Stack
          direction="column"
          sx={{
            flex: 1,
            height: '100%',
          }}
        >
          <Box
            sx={{
              p: 2,
              flex: 1,
              height: '100%',
            }}
          >
            <LotCardInfo lot={data} showTitle={true} showInlineLabels={true} />
          </Box>
          <Divider sx={{ opacity: 0.5 }} />

          <Stack
            direction="row"
            spacing={2}
            sx={{
              p: 2,
              justifyContent: 'space-between',
              height: '83.91px',
            }}
          >
            {seller != null && (
              <Stack
                sx={{
                  minWidth: 0,
                }}
              >
                <Typography variant="caption">{t('seller')}</Typography>
                <UserChip user={seller} />
              </Stack>
            )}

            {broker != null && (
              <Stack
                sx={{
                  minWidth: 0,
                }}
              >
                <Typography variant="caption">{t('Agent')}</Typography>
                <UserChip user={broker} />
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
});

const LotCardSkeleton = () => {
  return (
    <Card>
      <Skeleton
        variant="rectangular"
        sx={{
          paddingTop: '56.25%',
        }}
        height="30%"
        width="100%"
      />
      <CardActionArea sx={{ flexGrow: 1 }}>
        <Stack
          direction="column"
          sx={{
            flex: '1',
          }}
        >
          <Box
            sx={{
              p: 2,
            }}
          >
            <Stack spacing={1}>
              <Skeleton variant="rounded" width={230} height={14} />
              <Skeleton variant="rounded" width={230} height={18} />
              <Skeleton variant="rounded" width={160} height={24} />
              <Skeleton variant="rounded" width={80} height={16} />
              <Stack spacing={1} direction="row">
                <Skeleton variant="rounded" width={70} height={22} />
                <Skeleton variant="rounded" width={80} height={22} />
              </Stack>
            </Stack>
          </Box>
          <Divider sx={{ opacity: 0.5 }} />
          <Stack
            direction="row"
            sx={{
              p: 2,
              justifyContent: 'space-between',
            }}
          >
            <Stack spacing={0.8}>
              <Skeleton variant="rounded" width={40} height={14} />
              <Skeleton
                variant="rounded"
                width={100}
                height={32}
                sx={{
                  borderRadius: 16,
                }}
              />
            </Stack>
            <Stack spacing={0.8}>
              <Skeleton variant="rounded" width={40} height={14} />
              <Skeleton
                variant="rounded"
                width={100}
                height={32}
                sx={{
                  borderRadius: 16,
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

type GridViewProps = {
  lots?: GetLotsListQuery['lots'];
  totalCount?: number;
  listingLink: (id: string) => { pathname: string; search: string };
  loading?: boolean;
  queryParamsScope?: string;
};

const GridView = ({
  lots,
  totalCount,
  listingLink,
  loading,
  queryParamsScope,
}: GridViewProps) => {
  return (
    <RaGrid
      items={lots}
      loading={loading}
      totalCount={totalCount || 0}
      pageSize={GRID_PAGE_SIZE}
      getItemLink={
        listingLink as (id: string | number) => {
          pathname: string;
          search: string;
        }
      }
      renderSkeleton={LotCardSkeleton}
      renderItemCard={lot => <LotCard data={lot} />}
      queryParamsScope={queryParamsScope}
    />
  );
};

export const LISTINGS_DETAILED_VIEW_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingsDetailedView on lots {
    property {
      id
      floor_of_flat
      is_oriented_north
      is_oriented_south
      is_oriented_east
      is_oriented_west
      number_of_rooms
      number_of_bedrooms
      number_of_bathrooms
      number_of_toilets
      number_of_indoor_parkings
      number_of_outdoor_parkings
      living_surface
      built_surface
      usable_surface
      gross_floor_surface
      balcony_surface
      terrace_surface
      basement_surface
      garden_surface
      land_surface
    }
  }
`);

const getListingsTableViewColumns = (t: Translate): GridColDef<Lot>[] => [
  {
    field:
      'property.postcode,property.locality,property.route,property.street_number',
    headerName: t('Listing'),
    width: 250,
    display: 'flex',
    renderCell: ({ row }) => <ListingCell listing={row} />,
  },
  {
    field: 'sellers',
    headerName: t('Seller'),
    sortable: false,
    width: 200,
    renderCell: ({ row }) =>
      row.sellers.length > 0 && <UserInfo user={row.sellers[0].seller} />,
  },
  {
    field: 'broker.last_name,broker.first_name',
    headerName: t('Agent'),
    width: 200,
    renderCell: ({ row }) => row.broker && <UserInfo user={row.broker} />,
  },
  {
    field: 'status',
    headerName: t('Status'),
    width: 100,
    display: 'flex',
    renderCell: ({ row }) => (
      <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
        <Tag status={getLotStatusTagVariant(row.status)}>
          {row.lots_status.label ? row.lots_status.label : row.status}
        </Tag>
        <BlockedAdvertiserTag lot={row} />
      </Stack>
    ),
  },
  {
    field: 'signed_at,created_at',
    headerName: t('Signed'),
    width: 100,
    display: 'flex',
    renderCell: ({ row }) => (
      <Box>
        <Typography
          variant="body2"
          color={row.is_exclusive ? 'success.main' : 'grey.600'}
          sx={{
            fontWeight: row.is_exclusive ? 'bold' : 'normal',
          }}
        >
          {row.is_exclusive
            ? t('exclusive')
            : row.is_exclusive === false
            ? t('non-exclusive')
            : t('unspecified')}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          <TimeAgo
            dateString={row.signed_at ?? row.created_at}
            ignoreTime={row.signed_at != null}
          />
        </Typography>
      </Box>
    ),
  },
  {
    field: 'sale_price, rent_net',
    headerName: t('Price'),
    width: 200,
    align: 'right',
    display: 'flex',
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            lineHeight: '20px',
            fontWeight: 500,
          }}
        >
          {t('Price')}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            lineHeight: '20px',
          }}
        >
          {t('Appraisal')}
        </Typography>
      </Box>
    ),
    renderCell: ({ row }) => <ListingPriceCell row={row} />,
  },
  {
    field: 'enquiries_aggregate.max.created_at',
    headerName: t('Enquiries'),
    width: 100,
    display: 'flex',
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            lineHeight: '20px',
            fontWeight: 500,
          }}
        >
          {t('Enquiries')}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            lineHeight: '20px',
          }}
        >
          {t('Last enquiry')}
        </Typography>
      </Box>
    ),
    renderCell: ({ row }) => (
      <Box>
        <Typography
          variant="body2"
          sx={{
            display: 'inline-block',
          }}
        >
          {row.enquiries_aggregate.aggregate?.count ?? 0}
        </Typography>
        &nbsp;
        <Typography variant="caption">{t('enquiries')}</Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
          }}
        >
          <TimeAgo
            dateString={row.enquiries_aggregate.aggregate?.max?.created_at}
          />
        </Typography>
      </Box>
    ),
  },
];

const getListingsDetailedViewColumns = (
  t: Translate,
  locale: IntlLocale,
): GridColDef<Lot>[] => [
  {
    field:
      'property.postcode,property.locality,property.route,property.street_number',
    headerName: t('Listing'),
    width: 300,
    display: 'flex',
    renderCell: ({ row }) => <ListingCell listing={row} />,
  },
  {
    field: 'status',
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
          }}
        >
          {t('Status')}
        </Typography>
        <Typography variant="caption">{t('Published')}</Typography>
      </Box>
    ),
    width: 100,
    display: 'flex',
    renderCell: ({ row }) => {
      const livePortalListingsCount = row.portal_listings.filter(
        portalListing => portalListing.live,
      ).length;
      const isPublished = livePortalListingsCount > 0;

      return (
        <Stack
          spacing={0.5}
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Tag status={getLotStatusTagVariant(row.status)}>
            {row.lots_status.label ? row.lots_status.label : row.status}
          </Tag>
          <BlockedAdvertiserTag lot={row} />
          <Typography
            variant="caption"
            color={isPublished ? green[800] : red[800]}
            sx={{
              display: 'block',
              textAlign: 'right',
              lineHeight: '18px',
            }}
          >
            {isPublished ? t('online') : t('offline')}{' '}
            {isPublished && `(${livePortalListingsCount})`}
          </Typography>
        </Stack>
      );
    },
  },
  {
    field: 'property.floor_of_flat',
    headerName: t('Floor'),
    width: 100,
    valueGetter: (_, row) => row.property.floor_of_flat,
    valueFormatter: (value: Lot['property']['floor_of_flat']) =>
      value === 0 ? t('ground') : value,
  },
  {
    field:
      'property.is_oriented_north,property.is_oriented_south,property.is_oriented_east,property.is_oriented_west',
    headerName: t('Orientation'),
    width: 100,
    renderCell: ({ row }) => (
      <div>
        {extractPropertyOrientations(row.property)
          .map(value => getOrientationTranslation(t, value))
          .join(', ')}
      </div>
    ),
  },
  {
    field: 'property.number_of_rooms',
    headerName: t('Rooms'),
    width: 100,
    display: 'flex',
    renderCell: ({ row }) => (
      <div>
        {t('roomsShort', {
          count: row.property.number_of_rooms || 0,
        })}
        <Box sx={theme => theme.text.caption}>
          {t('numberOfBedroomsWithCount', {
            count: row.property.number_of_bedrooms || 0,
          })}
        </Box>
      </div>
    ),
  },
  {
    field: 'property.number_of_bathrooms',
    headerName: t('Bathrooms'),
    width: 100,
    display: 'flex',
    renderCell: ({ row }) => (
      <div>
        {t('numberOfBathroomsWithCount', {
          count: row.property.number_of_bathrooms || 0,
        })}
        <Box sx={theme => theme.text.caption}>
          {t('numberOfToiletsWithCount', {
            count: row.property.number_of_toilets || 0,
          })}
        </Box>
      </div>
    ),
  },
  {
    field: 'property.number_of_indoor_parkings',
    headerName: t('Parkings'),
    width: 100,
    display: 'flex',
    renderCell: ({ row }) => (
      <div>
        {t('numberOfIndoorParkingsWithCount', {
          count: row.property.number_of_indoor_parkings || 0,
        })}
        <Box sx={theme => theme.text.caption}>
          {t('numberOfOutdoorParkingsWithCount', {
            count: row.property.number_of_outdoor_parkings || 0,
          })}
        </Box>
      </div>
    ),
  },
  {
    field: 'property.living_surface',
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
          }}
        >
          {t('Housing')}
        </Typography>
        <Typography variant="caption">{t('livingSurfaceShort')}</Typography>
      </Box>
    ),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(
          property.living_surface ?? property.built_surface,
          t,
          locale,
        )}
      </Box>
    ),
  },
  {
    field: 'property.usable_surface',
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
          }}
        >
          {t('Housing')}
        </Typography>
        <Typography variant="caption">{t('usableSurfaceShort')}</Typography>
      </Box>
    ),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(property.usable_surface, t, locale)}
      </Box>
    ),
  },
  {
    field: 'property.gross_floor_surface',
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
          }}
        >
          {t('Housing')}
        </Typography>
        <Typography variant="caption">{t('grossFloorSurfaceShort')}</Typography>
      </Box>
    ),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(property.gross_floor_surface, t, locale)}
      </Box>
    ),
  },
  {
    field: 'property.balcony_surface',
    headerName: t('Balcony'),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(property.balcony_surface, t, locale)}
      </Box>
    ),
  },
  {
    field: 'property.terrace_surface',
    headerName: t('Terrace'),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(property.terrace_surface, t, locale)}
      </Box>
    ),
  },
  {
    field: 'property.basement_surface',
    headerName: t('Basement'),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(property.basement_surface, t, locale)}
      </Box>
    ),
  },
  {
    field: 'property.garden_surface',
    headerName: t('Garden'),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(property.garden_surface, t, locale)}
      </Box>
    ),
  },
  {
    field: 'property.land_surface',
    headerName: t('Land'),
    width: 100,
    renderCell: ({ row: { property } }) => (
      <Box sx={theme => theme.text.ellipsis}>
        {formatSurfaceValue(property.land_surface, t, locale)}
      </Box>
    ),
  },
  {
    field: 'sale_price',
    headerName: t('Price'),
    width: 150,
    display: 'flex',
    renderCell: ({ row }) => <ListingPriceCell row={row} />,
  },
];

export const Listings = ({
  developmentId,
  detailedView = false,
  hideTitle = false,
  reorderable = false,
  queryParamsScope,
}: ListingsProps) => {
  const [searchParams] = useScopedSearchParams(queryParamsScope);
  const { t, locale } = useLocale();
  const navigate = useNavigateWithSearchParams();
  const { editMode, selectedRows, setSelectedRows } = useBulkEditMode();
  const { me } = useAppData();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const { variables } = useMemo(
    () => initialListingsListQuery(searchParams, reorderable, developmentId),
    [searchParams, reorderable, developmentId],
  );
  const { where, order_by, limit, offset } = variables ?? {};

  const viewType = searchParams.get('view') ?? 'grid';

  const listingLink = (id: string) => {
    return {
      pathname: `./${id}`,
      search: toScopedSearchParams(searchParams, queryParamsScope).toString(),
    };
  };

  const { data, loading, error, refetch } = useQuery(GET_LOTS_LIST, {
    variables: {
      where,
      order_by,
      limit,
      offset,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: countData } = useQuery(GET_LOTS_LIST_COUNT, {
    variables: {
      where,
    },
  });

  const [reorderListing] = useMutation(REORDER_LOTS, {
    update: (cache, { data: reorderData }, { variables }) => {
      if (variables == null || reorderData == null) {
        return;
      }

      const targetOrder = variables.target_position;
      const listingRef = cache.identify({
        __typename: 'lots',
        id: variables.id,
      });

      const currentListing = cache.readFragment<{
        id: string;
        order_nr: number;
      }>({
        id: listingRef,
        fragment: gql(/* GraphQL */ `
          fragment CurrentListing on lots {
            id
            order_nr
          }
        `),
      });

      if (currentListing == null) {
        return;
      }

      const lotsQuery = cache.readQuery<GetLotsListQuery>({
        query: GET_LOTS_LIST,
        variables: {
          where,
          order_by,
          limit,
          offset,
        },
      });

      if (lotsQuery == null) {
        return;
      }

      const oldOrder = currentListing.order_nr;

      const updatedLots = lotsQuery?.lots
        .map(lot => {
          if (lot.id !== currentListing.id) {
            if (
              targetOrder > oldOrder &&
              lot.order_nr <= targetOrder &&
              lot.order_nr > oldOrder
            ) {
              return { ...lot, order_nr: lot.order_nr - 1 };
            }

            if (
              targetOrder < oldOrder &&
              lot.order_nr >= targetOrder &&
              lot.order_nr < oldOrder
            ) {
              return { ...lot, order_nr: lot.order_nr + 1 };
            }

            return lot;
          } else {
            return { ...lot, order_nr: targetOrder };
          }
        })
        .sort((a, b) => a.order_nr - b.order_nr);

      cache.writeQuery<GetLotsListQuery>({
        query: GET_LOTS_LIST,
        variables: {
          where,
          order_by,
          limit,
          offset,
        },
        data: {
          ...lotsQuery,
          lots: updatedLots,
        },
      });

      cache.modify({
        id: listingRef,
        fields: {
          order_nr: () => targetOrder,
        },
      });
    },
  });
  const [deleteLots, deleteOptions] = useMutation(DELETE_LOTS, {
    onCompleted: () => {
      refetch();
    },
  });

  const handleDelete = (ids: string[]) => {
    deleteLots({
      variables: {
        ids,
      },
    });
  };

  const rows: GridRowsProp<Lot> = data?.lots ?? [];

  const { togglePortalListing, WarningSnackbar, ErrorDialog } =
    useTogglePortalListing();

  const columns = useMemo<GridColDef<Lot>[]>(() => {
    const cols = detailedView
      ? getListingsDetailedViewColumns(t, locale)
      : getListingsTableViewColumns(t);

    // Create an array of my teams ids to sort the portals
    const myTeams = data?.me?.teams_users.map(({ team_id }) => team_id);
    if (data?.portals.length && !detailedView) {
      const portalColumns = [...data.portals]
        // Sort alphabetically first
        .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
        // Then sort my team's portal first and other portals last (only visible to admin users)
        .sort((a, b) => {
          const portalATeams = [
            a.team_id,
            ...a.teams_portals.map(({ team_id }) => team_id),
          ];
          const portalBTeams = [
            b.team_id,
            ...b.teams_portals.map(({ team_id }) => team_id),
          ];
          const aIsMine = myTeams?.some(id => portalATeams.includes(id));
          const bIsMine = myTeams?.some(id => portalBTeams.includes(id));
          return aIsMine && !bIsMine ? -1 : !aIsMine && bIsMine ? 1 : 0;
        })
        .map((portal): GridColDef<Lot> => {
          return {
            field: `portals.${portal.id}`,
            headerAlign: 'center',
            renderHeader: () => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: '20px',
                    fontWeight: 500,
                  }}
                >
                  {portal.name ?? 'Portal'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    lineHeight: '20px',
                  }}
                >
                  {portal.team?.name ?? 'shared'} (
                  {portal.portal_listings_aggregate.aggregate?.count ?? 0}/
                  {portal.quota ?? '∞'})
                </Typography>
              </Box>
            ),
            width: 100,
            sortable: false,
            align: 'center',
            display: 'flex',
            renderCell: ({ row }) => {
              const portalListing = row.portal_listings.find(
                ({ portal_id }: { portal_id: string }) =>
                  portal_id === portal.id,
              );
              const canEdit = canEditListing(me, row);
              // As admin we have the same permissions as the broker (no portal leakage from other portals)
              const canPublish =
                userCanPublishPortalListing(
                  me?.is_admin
                    ? row.broker
                      ? { teams_users: row.broker.active_teams_users }
                      : null
                    : me,
                  portal,
                  portalListing?.live ?? false,
                ) && canEdit;
              const enquiriesCount = row.enquiries.filter(
                ({ portal_id }) => portal_id === portal.id,
              ).length;
              return (
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    marginTop: -10,
                  }}
                >
                  <Stack direction="row" spacing={0}>
                    <LoadingCheckBox
                      disabled={!canPublish}
                      checked={portalListing?.live ?? false}
                      onClick={async e => {
                        e.stopPropagation();
                        const previousValue = portalListing?.live ?? false;

                        return togglePortalListing({
                          variables: {
                            portal_id: portal.id,
                            lot_id: row.id,
                          },
                        }).then(
                          ({ data }) =>
                            data?.toggle_portal_listing?.live ?? previousValue,
                        );
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    style={{
                      position: 'absolute',
                      bottom: -10,
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {enquiriesCount}
                  </Typography>
                </div>
              );
            },
          };
        });
      cols.push(...portalColumns);
    }

    return cols;
  }, [
    t,
    locale,
    detailedView,
    data?.portals,
    data?.me?.teams_users,
    me,
    togglePortalListing,
  ]);

  const pinnedColumns = useMemo(
    () => ({
      left: isMobile
        ? undefined
        : [
            GRID_REORDER_COL_DEF.field,
            GRID_CHECKBOX_SELECTION_COL_DEF.field,
            'property.postcode,property.locality,property.route,property.street_number',
          ],
    }),
    [isMobile],
  );

  const columnVisibilityModel = useMemo(
    () => ({
      [GRID_CHECKBOX_SELECTION_COL_DEF.field]: editMode,
    }),
    [editMode],
  );

  return (
    <>
      {!hideTitle && (
        <SmartListTitle tableName="lots" defaultTitle={t('Listings')} />
      )}
      <ListToolbar
        tableName="lots"
        newLink={{
          pathname: 'new',
          search: toScopedSearchParams(
            searchParams,
            queryParamsScope,
          ).toString(),
        }}
        refetch={refetch}
        importProps={{
          developmentId,
        }}
        queryParamsScope={queryParamsScope}
        searchComponent={
          developmentId != null ? undefined : (
            <LotSelect
              InputProps={{
                startAdornment: <SelectStartIcon icon={Search} />,
              }}
              sx={{ maxWidth: 350, minWidth: 100, flexGrow: 1, ml: 1 }}
              autoFocus
              onChange={lotId => {
                if (!lotId) {
                  return;
                }
                navigate(listingLink(lotId));
              }}
            />
          )
        }
        quickFilters={quickFilters(t, me)}
        viewLinks={[
          { name: 'grid', icon: LayoutGrid },
          { name: 'list', icon: Rows3 },
        ].map(({ name, icon: Icon }) => ({
          name,
          icon: <Icon size={18} strokeWidth={viewType === name ? 2.5 : 1.5} />,
        }))}
        ExportDialog={ListingsExport}
        ImportDialog={ListingsImport}
        bulkActions={
          <>
            {me?.is_admin && (
              <LoadingButton
                disabled={(getIncludedIds(selectedRows)?.length ?? 0) === 0}
                disableElevation
                onClick={() => {
                  const ids = getIncludedIds(selectedRows);
                  if (ids == null) {
                    return;
                  }
                  handleDelete(ids as string[]);
                }}
                loading={deleteOptions.loading}
                loadingPosition="start"
                startIcon={<Trash2Icon />}
                color="error"
              >
                {t('Delete')}
              </LoadingButton>
            )}
          </>
        }
      />

      {ErrorDialog}
      {WarningSnackbar}
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : viewType === 'list' ? (
        <RaDataGrid
          queryParamsScope={queryParamsScope}
          columns={columns}
          rows={rows}
          rowCount={countData?.lots_aggregate.aggregate?.count ?? 0}
          onRowClick={({ id }, e) => {
            if (e.metaKey || e.ctrlKey) {
              window.open(listingLink(id as string).pathname, '_blank');
              return;
            }
            navigate(listingLink(id as string));
          }}
          loading={loading}
          rowHeight={60}
          checkboxSelection={editMode}
          disableRowSelectionOnClick
          onRowSelectionModelChange={newSelection => {
            setSelectedRows(newSelection);
          }}
          columnVisibilityModel={columnVisibilityModel}
          rowSelectionModel={selectedRows}
          pinnedColumns={pinnedColumns}
          rowReordering={reorderable}
          onRowOrderChange={newModel => {
            reorderListing({
              variables: {
                id: newModel.row.id,
                target_position: rows[newModel.targetIndex].order_nr,
              },
            });
          }}
        />
      ) : (
        <GridView
          lots={data?.lots}
          listingLink={listingLink}
          totalCount={countData?.lots_aggregate.aggregate?.count}
          loading={loading}
          queryParamsScope={queryParamsScope}
        />
      )}
      <Suspense fallback={null}>
        <Routes>
          <Route
            path="new/*"
            element={
              <CreateListingDrawer
                developmentId={developmentId}
                onListingCreated={() => {
                  refetch();
                }}
              />
            }
          />
          <Route path=":lotId/*" element={<ListingDrawer />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default Listings;
